import React, { useState } from 'react';
import { Menu, X, Settings, Layers, User, Clock, HelpCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';

function Sidebar({ collapsed = false, onToggle }) {
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    if (onToggle) {
      onToggle(newCollapsedState);
    }
  };

  // This array is now used to render the nav items
  const navItems = [
    {
      name: 'Forge',
      icon: <Layers className="h-5 w-5" />,
      href: '/',
      active: location.pathname === '/',
    },
    {
      name: 'Documentation',
      icon: <HelpCircle className="h-5 w-5" />,
      href: '/help',
      active: location.pathname === '/help',
    },
    {
      name: 'History',
      icon: <Clock className="h-5 w-5" />,
      href: '#',
      active: false,
      soon: true,
    },
    {
      name: 'Settings',
      icon: <Settings className="h-5 w-5" />,
      href: '#',
      active: false,
      soon: true,
    },
  ];

  return (
    <div className={`bg-gray-900 text-white transition-all duration-300 flex flex-col ${isCollapsed ? 'w-16' : 'w-64'}`}>
      {/* Header with toggle */}
      <div className="p-4 flex items-center justify-between border-b border-gray-800">
        {isCollapsed ? (
          <div className="flex justify-center items-center">
            <span className="text-xl font-bold text-blue-500">UI</span>
          </div>
        ) : (
          <div className="flex items-center">
            <span className="text-xl font-bold text-blue-500">UI</span>
            <span className="text-xl font-bold text-white ml-1">Forge</span>
          </div>
        )}
        <button 
          onClick={toggleCollapse} 
          className="p-1 rounded-md hover:bg-gray-800"
        >
          {isCollapsed ? <Menu size={20} /> : <X size={20} />}
        </button>
      </div>
      
      {/* User Profile - REMOVED FROM HERE */}
      {/* {user && ( ... original profile block ... )} */}
      
      {/* Nav Items */}
      <div className="flex-1 py-4">
        {/* Use the navItems array to render navigation */}
        {navItems.map((item) => (
          <NavItem 
            key={item.name}
            icon={item.icon} 
            label={item.soon ? (
              <div className="flex items-center">
                <span>{item.name}</span>
                <span className="ml-2 px-2 py-0.5 text-xs bg-gray-700 text-gray-300 rounded">Coming soon</span>
              </div>
            ) : item.name} 
            collapsed={isCollapsed} 
            active={item.active}
            onClick={() => item.soon ? null : navigate(item.href)}
            disabled={item.soon}
          />
        ))}
      </div>
      
      {/* Bottom actions */}
      <div className="py-4 border-t border-gray-800">
        {user ? (
          <>
             {/* User Profile - MOVED HERE */}
            <div className={`py-4 px-4 ${isCollapsed ? 'flex justify-center' : ''}`}> {/* Removed border-b */}
              {isCollapsed ? (
                user.picture ? (
                  <img src={user.picture} alt="Profile" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                    {user.name ? user.name.charAt(0) : 'U'}
                  </div>
                )
              ) : (
                <div className="flex items-center">
                  {user.picture ? (
                    <img src={user.picture} alt="Profile" className="w-8 h-8 rounded-full mr-3" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium mr-3">
                       {user.name ? user.name.charAt(0) : 'U'}
                    </div>
                  )}
                  <div className="flex-1 overflow-hidden">
                    <p className="text-sm font-medium truncate">{user.name || 'User'}</p>
                    <p className="text-xs text-gray-400 truncate">{user.email || 'No email'}</p>
                  </div>
                </div>
              )}
            </div>
          
            {/* Profile Button */}
            <button 
              onClick={() => navigate('/profile')}
              className={`flex items-center py-2 px-4 w-full text-left hover:bg-gray-800 ${location.pathname === '/profile' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
            >
              <div className="mr-3"><User size={20} /></div>
              {!isCollapsed && <span>Profile</span>}
            </button>
          </>
        ) : (
          // Show Sign In button when not logged in
          <button 
            onClick={() => navigate('/auth')}
            className={`flex items-center py-2 px-4 w-full text-left hover:bg-gray-800 text-gray-400 hover:text-white cursor-pointer`}
          >
            <div className="mr-3"><User size={20} /></div>
            {!isCollapsed && <span>Sign In</span>}
          </button>
        )}
      </div>

      {/* Privacy Policy Link (only when not collapsed) */}
      {!isCollapsed && (
        <div className="px-4 py-2 mt-auto">
          <Link 
            to="/privacy-policy"
            className="text-xs text-gray-500 hover:text-gray-400 hover:underline"
          >
            Privacy Policy
          </Link>
        </div>
      )}

    </div>
  );
}

// Helper component for nav items
function NavItem({ icon, label, collapsed, active, onClick, disabled }) {
  return (
    <div 
      className={`flex items-center py-2 px-4 ${
        disabled ? 'cursor-not-allowed text-gray-500' : 'cursor-pointer'
      } ${
        active ? 'bg-gray-800' : disabled ? '' : 'hover:bg-gray-800'
      }`}
      onClick={!disabled ? onClick : undefined}
    >
      <div className="mr-3">{icon}</div>
      {!collapsed && (typeof label === 'string' ? <span>{label}</span> : label)}
    </div>
  );
}

export default Sidebar; 